import { useCallback, useEffect, useRef, useState, type ChangeEvent, type PointerEvent } from 'react';

/**
 * Recorte do logo do sistema. O quadro e largo (3:1) porque e assim que o logo
 * aparece na barra e na tela de login — e porque o problema tipico de um
 * logotipo exportado e justamente a sobra de margem em cima e embaixo.
 *
 * A imagem comeca preenchendo o quadro ("cover"); arrastar move e o controle
 * de zoom aproxima. O recorte sai em 900x300 PNG.
 */

const PROPORCAO = 3;
const SAIDA_LARGURA = 900;

interface Props {
  onSave: (dataUrl: string) => void | Promise<void>;
  onCancel: () => void;
  salvando: boolean;
}

export function LogoCropper({ onSave, onCancel, salvando }: Props) {
  const quadroRef = useRef<HTMLDivElement | null>(null);
  const arrastando = useRef<{ x: number; y: number } | null>(null);
  const [imagem, setImagem] = useState<HTMLImageElement | null>(null);
  /** A URL do blob vive enquanto a previa estiver na tela (ver `escolher`). */
  const urlRef = useRef<string | null>(null);
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [larguraQuadro, setLarguraQuadro] = useState(420);

  const alturaQuadro = larguraQuadro / PROPORCAO;

  useEffect(() => {
    const medir = () => {
      if (quadroRef.current) setLarguraQuadro(quadroRef.current.clientWidth);
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [imagem]);

  /** Escala minima para a imagem cobrir o quadro inteiro (sem faixa vazia). */
  const escalaBase = imagem
    ? Math.max(larguraQuadro / imagem.naturalWidth, alturaQuadro / imagem.naturalHeight)
    : 1;
  const larguraExibida = imagem ? imagem.naturalWidth * escalaBase * zoom : 0;
  const alturaExibida = imagem ? imagem.naturalHeight * escalaBase * zoom : 0;

  const limitar = useCallback(
    (proposto: { x: number; y: number }) => {
      const maxX = Math.max(0, (larguraExibida - larguraQuadro) / 2);
      const maxY = Math.max(0, (alturaExibida - alturaQuadro) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, proposto.x)),
        y: Math.min(maxY, Math.max(-maxY, proposto.y)),
      };
    },
    [larguraExibida, alturaExibida, larguraQuadro, alturaQuadro],
  );

  // Ao aproximar, o recorte nao pode escapar do quadro.
  useEffect(() => {
    setOffset((atual) => limitar(atual));
  }, [limitar]);

  // A URL do blob so pode ser liberada quando a previa sair da tela: revogar
  // logo apos o `onload` deixava o <img> da previa sem imagem para exibir.
  useEffect(() => {
    return () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, []);

  function escolher(event: ChangeEvent<HTMLInputElement>) {
    const arquivo = event.target.files?.[0];
    event.target.value = '';
    if (!arquivo) return;
    setNomeArquivo(arquivo.name);
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    const url = URL.createObjectURL(arquivo);
    urlRef.current = url;
    const img = new Image();
    img.onload = () => {
      setImagem(img);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };
    img.src = url;
  }

  function aoPegar(event: PointerEvent<HTMLDivElement>) {
    if (!imagem) return;
    arrastando.current = { x: event.clientX - offset.x, y: event.clientY - offset.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function aoMover(event: PointerEvent<HTMLDivElement>) {
    const inicio = arrastando.current;
    if (!inicio) return;
    setOffset(limitar({ x: event.clientX - inicio.x, y: event.clientY - inicio.y }));
  }

  function aoSoltar(event: PointerEvent<HTMLDivElement>) {
    arrastando.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function recortar() {
    if (!imagem) return;
    const canvas = document.createElement('canvas');
    canvas.width = SAIDA_LARGURA;
    canvas.height = Math.round(SAIDA_LARGURA / PROPORCAO);
    const contexto = canvas.getContext('2d');
    if (!contexto) return;

    // Mesma conta da previa, multiplicada pela razao entre saida e quadro.
    const fator = canvas.width / larguraQuadro;
    const x = (larguraQuadro / 2 + offset.x - larguraExibida / 2) * fator;
    const y = (alturaQuadro / 2 + offset.y - alturaExibida / 2) * fator;
    contexto.drawImage(imagem, x, y, larguraExibida * fator, alturaExibida * fator);
    void onSave(canvas.toDataURL('image/png'));
  }

  return (
    <div>
      <div className="field">
        <label htmlFor="logo-arquivo">Arquivo do logo</label>
        <input id="logo-arquivo" type="file" accept="image/png,image/jpeg,image/webp" onChange={escolher} />
        <span className="small muted">
          PNG, JPG ou WebP. Depois de escolher, arraste para posicionar e use o zoom.
        </span>
      </div>

      {imagem ? (
        <>
          <div
            ref={quadroRef}
            className="cropper"
            style={{ height: alturaQuadro }}
            onPointerDown={aoPegar}
            onPointerMove={aoMover}
            onPointerUp={aoSoltar}
            onPointerCancel={aoSoltar}
          >
            <img
              src={imagem.src}
              alt=""
              draggable={false}
              style={{
                width: larguraExibida,
                height: alturaExibida,
                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
              }}
            />
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <label htmlFor="logo-zoom">Zoom</label>
            <input
              id="logo-zoom"
              type="range"
              min={1}
              max={4}
              step={0.02}
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
            />
            <span className="small muted">
              {nomeArquivo} · {imagem.naturalWidth}×{imagem.naturalHeight} px
            </span>
          </div>

          <div className="row">
            <button type="button" className="btn btn-primary" onClick={recortar} disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar logo'}
            </button>
            <button type="button" className="btn" onClick={onCancel} disabled={salvando}>
              Cancelar
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
